import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore, type ReactNode } from 'react'
import { SandboxBanner } from './SandboxBanner'
import { InstallAppPrompt } from '../components/InstallAppPrompt'
import { PushPrompt } from '../components/PushPrompt'
import { Link, useLocation } from 'react-router-dom'
import { logout, type LoginResponse } from '../api/auth'
import { partnersApi } from '../api/partners'
import { getFeatureFlags, resetFeatureFlagCache, useFeatureFlags } from '../lib/featureFlags'
import { NAV_GROUPS, resolveActive, visibleAccountItems, visibleGroups, type NavGroupId } from '../navigation/menu.ts'
import { getPageTitle } from '../navigation/pageTitles'
import { NavigationDrawer, type NavOpenMode } from '../components/ui/navigation/NavigationDrawer'
import { NavigationRail, type RailDestination } from '../components/ui/navigation/NavigationRail'
import { NavigationBar } from '../components/ui/navigation/NavigationBar'
import { useVirtualKeyboardOpen } from '../components/ui/navigation/useVirtualKeyboardOpen'
import { FabMenu } from '../components/ui/navigation/FabMenu'
import { barDestinationsFor, isCreateRoute, quickActionsFor } from './quickActions'
import { NAV_SEARCH_FOCUS_EVENT } from '../components/ui/navigation/navSearchModel'
import { AppBar } from './AppBar'
import { hasStandardRail, useWindowClass } from './breakpoints'
import { ThemePanel } from './ThemePanel'
import { syncSegmentAttributes, useViewportSegments } from './segments'
import { usePaneLayoutPresent } from './panes'
import { Icon, SnackbarProvider, suppressTooltipsBriefly } from '../components/ui'
import { initTheme, resolvedMode, setBranchAccent, setThemeMode } from '../theme/theme'
import { GENERAL, resetBranchCache, useBranch } from '../lib/branch'
import { BranchChip } from './BranchChip'
import './layout.css'

/* ── Preferenze in localStorage: sempre in try/catch (modalità privata, dati bloccati…) ── */
function lsRead(key: string): string | null { try { return localStorage.getItem(key) } catch { return null } }
function lsWrite(key: string, value: string): void { try { localStorage.setItem(key, value) } catch { /* storage non disponibile */ } }

const KNOWN_GROUPS = new Set<string>(NAV_GROUPS.map(g => g.id))

function readOpenIds(key: string): Set<NavGroupId> | null {
  const raw = lsRead(key)
  if (raw === null) return null
  try {
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return null
    return new Set(parsed.filter((x): x is NavGroupId => typeof x === 'string' && KNOWN_GROUPS.has(x)))
  } catch { return null }
}

/* ── Tema scuro/chiaro attuale (segue anche i cambi fatti dal pannello Personalizza) ── */
function useIsDark(): boolean {
  const subscribe = useCallback((cb: () => void) => {
    const mo = new MutationObserver(cb)
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] })
    return () => mo.disconnect()
  }, [])
  return useSyncExternalStore(subscribe, () => resolvedMode() === 'dark', () => true)
}

export function AppShell({ auth, onLogout, children }: { auth: LoginResponse; onLogout: () => void; children: ReactNode }) {
  const location = useLocation()
  const navClass = useWindowClass()
  const standardRail = hasStandardRail(navClass)
  const isDark = useIsDark()
  // Pieghevoli: attributi e variabili dei segmenti su <html>; libro separante = nav nel segmento 0 (se la pagina non ha PaneLayout).
  useEffect(() => syncSegmentAttributes(), [])
  const seg = useViewportSegments()
  const hasPanes = usePaneLayoutPresent()
  const bookList = seg.axis === 'horizontal' && seg.separating && !hasPanes
  const [themePanelOpen, setThemePanelOpen] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const modalRef = useRef<HTMLDialogElement>(null)
  const mainRef = useRef<HTMLElement>(null)

  const openKey = 'ecrm_nav_open_u' + auth.id
  const collapsedKey = 'ecrm_nav_collapsed_u' + auth.id
  const [railCollapsed, setRailCollapsed] = useState(() => lsRead(collapsedKey) === '1')
  const [mode, setMode] = useState<NavOpenMode>(() => (lsRead('ecrm_nav_mode') === 'multi' ? 'multi' : 'single'))

  // F-059: mostra "Il mio conto socio" solo a chi è collegato a un socio (GET /api/partners/me → 200).
  const [hasPartnerAccount, setHasPartnerAccount] = useState(false)
  useEffect(() => {
    let cancelled = false
    void partnersApi.me().then(me => { if (!cancelled) setHasPartnerAccount(me !== null) })
    return () => { cancelled = true }
  }, [])

  const flags = useFeatureFlags()
  // ADR-019 (Equal Point): con il flag acceso il menu segue il ramo scelto e gli accenti prendono il colore del ramo
  // (sfondi e superfici restano quelli Equal); la Generale torna al viola.
  const equalPoint = flags['equalpoint.rami'] === true
  const { mine: myLines, branch } = useBranch(equalPoint)
  const branchColor = branch && branch !== GENERAL ? myLines?.lines.find(l => l.slug === branch)?.color ?? null : null
  // Solo quando i rami sono arrivati (o il flag risulta spento): la ricarica parte dal colore ricordato senza lampeggiare.
  // Layout effect: nel cambio di ramo animato il nuovo colore deve esserci già quando si fotografa la pagina nuova.
  useLayoutEffect(() => { if (myLines) setBranchAccent(branchColor) }, [myLines, branchColor])
  useEffect(() => {
    let alive = true
    void getFeatureFlags().then(f => { if (alive && f['equalpoint.rami'] !== true) setBranchAccent(null) })
    return () => { alive = false }
  }, [])
  const groups = useMemo(() => visibleGroups({ role: auth.role, hasPartnerAccount, flags, branch }), [auth.role, hasPartnerAccount, flags, branch])
  const active = useMemo(() => resolveActive(location.pathname, location.search, groups), [location.pathname, location.search, groups])

  // Gruppi aperti: default al primo accesso = solo quello della pagina corrente.
  const [openIds, setOpenIds] = useState<Set<NavGroupId>>(() => readOpenIds(openKey) ?? new Set(active.groupId ? [active.groupId] : []))
  useEffect(() => {
    // A ogni cambio di rotta il gruppo attivo viene aggiunto (in modalità "uno alla volta" sostituisce gli altri).
    const id = active.groupId; if (!id) return
    setOpenIds(prev => {
      if (mode === 'single') return prev.size === 1 && prev.has(id) ? prev : new Set([id])
      return prev.has(id) ? prev : new Set(prev).add(id)
    })
  }, [active.groupId, mode])
  useEffect(() => { lsWrite(openKey, JSON.stringify([...openIds])) }, [openKey, openIds])

  const toggleGroup = useCallback((id: NavGroupId) => {
    setOpenIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else { if (mode === 'single') next.clear(); next.add(id) }
      return next
    })
  }, [mode])

  function changeMode(m: NavOpenMode) {
    setMode(m); lsWrite('ecrm_nav_mode', m)
    if (m === 'single') setOpenIds(prev => (prev.size <= 1 ? prev : new Set(active.groupId && prev.has(active.groupId) ? [active.groupId] : [[...prev][0] as NavGroupId])))
  }

  function toggleRail() {
    setRailCollapsed(c => { lsWrite(collapsedKey, c ? '0' : '1'); return !c })
  }

  // Tema: applica e ascolta prefers-color-scheme.
  useEffect(() => initTheme(), [])

  // document.title dal titolo di rotta.
  useEffect(() => {
    const t = getPageTitle(location.pathname)
    document.title = t === 'Equal CRM' ? t : `${t} · Equal CRM`
  }, [location.pathname])

  // La modal expanded rail si chiude a ogni navigazione e quando compare la rail standard (large).
  useEffect(() => { setModalOpen(false) }, [location.pathname, location.search])
  useEffect(() => { if (standardRail && !railCollapsed) setModalOpen(false) }, [standardRail, railCollapsed])
  useEffect(() => {
    const el = modalRef.current; if (!el) return
    if (modalOpen) {
      el.removeAttribute('data-closing')
      if (!el.open) {
        suppressTooltipsBriefly()
        el.showModal()
        // Il focus iniziale e' sul campo "Cerca nel menu" (non sul bottone Chiudi).
        el.querySelector<HTMLElement>('.md-nav-search__input')?.focus()
      }
      return
    }
    if (!el.open) return
    // Uscita: 200ms emphasized-accelerate (a zero con movimento ridotto), poi close().
    const reduce = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    el.setAttribute('data-closing', '')
    const t = window.setTimeout(() => { el.removeAttribute('data-closing'); if (el.open) { suppressTooltipsBriefly(); el.close() } }, reduce ? 0 : 200)
    return () => window.clearTimeout(t)
  }, [modalOpen, navClass, railCollapsed])

  // Alt+M: focus sul campo "Cerca nel menu".
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (!(e.altKey && !e.ctrlKey && !e.metaKey && e.code === 'KeyM')) return
      e.preventDefault()
      const fire = () => window.dispatchEvent(new Event(NAV_SEARCH_FOCUS_EVENT))
      if (standardRail && !railCollapsed) fire()
      else {
        if (standardRail) { setRailCollapsed(false); lsWrite(collapsedKey, '0') } else setModalOpen(true)
        setTimeout(fire, 120)
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [standardRail, railCollapsed, collapsedKey])

  async function handleLogout() { await logout(); resetFeatureFlagCache(); resetBranchCache(); setBranchAccent(null); onLogout() }

  const drawerProps = {
    groups, accountItems: visibleAccountItems({ role: auth.role, hasPartnerAccount, flags }), openIds, onToggleGroup: toggleGroup,
    activeGroupId: active.groupId, activeItemId: active.itemId,
    mode, onModeChange: changeMode, onCollapseAll: () => setOpenIds(new Set<NavGroupId>()),
    onLogout: () => void handleLogout(),
  }

  const showStandardDrawer = bookList || (standardRail && !railCollapsed)
  const showRail = !showStandardDrawer && navClass !== 'compact'

  // Navigation bar in basso (compact) e FAB menu delle azioni rapide (globale).
  const keyboardOpen = useVirtualKeyboardOpen()
  const barDests = useMemo(() => barDestinationsFor(groups), [groups])
  const quick = useMemo(() => quickActionsFor(groups), [groups])
  const showNavBar = navClass === 'compact' && !showStandardDrawer && barDests.length >= 3
  const fabAvailable = quick.length > 0 && !isCreateRoute(location.pathname)
  const fabIn: 'drawer' | 'rail' | 'floating' | null = !fabAvailable ? null
    // Libro separante: il drawer sta nel segmento 0, il FAB flottante nel segmento 1 (in basso a destra del main).
    : bookList ? 'floating' : showStandardDrawer ? 'drawer' : showRail ? 'rail' : 'floating'
  const floatingFab = fabIn === 'floating' && !keyboardOpen
  const navBarActive = barDests.find(d => d.id === active.itemId)?.id
    ?? barDests.find(d => { const p = d.to.split('?')[0] ?? d.to; return location.pathname === p || location.pathname.startsWith(p + '/') })?.id
    ?? null
  // Rail compresso: stesse destinazioni della barra in basso + quella del gruppo attivo (max 5), il resto nel menu.
  const railDests = useMemo<RailDestination[]>(() => {
    const out: RailDestination[] = barDests.map(d => ({ ...d, active: d.id === navBarActive }))
    if (!out.some(d => d.active)) {
      const g = groups.find(x => x.id === active.groupId)
      const first = g?.items[0]
      if (g && first) out.push({ id: 'group-' + g.id, to: first.to, label: g.shortLabel, icon: g.icon, active: true })
    }
    return out
  }, [barDests, navBarActive, groups, active.groupId])
  // Nel menu esteso un solo bottone chiaro, "Nuovo contratto"; lead, clienti e aziende si creano dalle loro liste.
  const newContract = quick.find(a => a.id === 'new-contract')
  const fabNode = (placement: 'drawer' | 'rail') => placement === 'drawer' && newContract
    ? <Link to={newContract.to} className="ecrm-new-contract"><Icon name="add" size={20} />{newContract.label}</Link>
    : <FabMenu actions={quick} placement={placement} />

  return (
    <SnackbarProvider>
      <div className="md-shell" data-nav={navClass} data-collapsed={!bookList && standardRail && railCollapsed} data-navbar={showNavBar && !keyboardOpen ? 'true' : undefined} data-fab={floatingFab ? 'floating' : undefined}>
        <a
          className="md-skip"
          href="#main-content"
          onClick={e => { e.preventDefault(); mainRef.current?.focus() }}
        >
          Vai al contenuto
        </a>

        {showStandardDrawer && (
          <aside className="md-shell__drawer">
            <NavigationDrawer {...drawerProps} onCollapse={bookList ? undefined : toggleRail} fabSlot={fabIn === 'drawer' ? fabNode('drawer') : undefined} />
          </aside>
        )}

        {showRail && (
          <NavigationRail
            destinations={railDests}
            menuIcon="menu"
            menuLabel={standardRail ? 'Espandi menu' : 'Apri menu completo'}
            onMenu={standardRail ? toggleRail : () => setModalOpen(true)}
            fabSlot={fabIn === 'rail' ? fabNode('rail') : undefined}
          />
        )}

        <div className="md-shell__body">
          <SandboxBanner />
          <AppBar
            navClass={navClass}
            hasNavBar={showNavBar}
            forceNarrow={bookList && (seg.segments[1]?.width ?? 999) < 360}
            onMenu={() => setModalOpen(true)}
            menuDialogOpen={modalOpen}
            themeIsDark={isDark}
            onQuickTheme={() => setThemeMode(resolvedMode() === 'dark' ? 'light' : 'dark')}
            onThemePanel={() => setThemePanelOpen(true)}
            userName={auth.name}
            userRole={auth.role}
            onLogout={() => void handleLogout()}
            branchSlot={myLines && branch ? <BranchChip mine={myLines} branch={branch} /> : undefined}
          />
          <main id="main-content" className="md-shell__main" tabIndex={-1} ref={mainRef}>
            <InstallAppPrompt />
            <PushPrompt />
            {children}
          </main>
        </div>

        {floatingFab && <FabMenu actions={quick} placement="floating" />}
        {showNavBar && (
          <NavigationBar destinations={barDests} activeId={navBarActive} onMenu={() => setModalOpen(true)} menuOpen={modalOpen} />
        )}

        {!showStandardDrawer && (
          <dialog
            ref={modalRef}
            className="md-drawer-modal"
            aria-label="Menu"
            onCancel={e => { e.preventDefault(); setModalOpen(false) }}
            onClose={() => setModalOpen(false)}
            onClick={e => { if (e.target === e.currentTarget) setModalOpen(false) }}
          >
            <NavigationDrawer {...drawerProps} onNavigate={() => setModalOpen(false)} onClose={() => setModalOpen(false)} />
          </dialog>
        )}

        <ThemePanel open={themePanelOpen} onClose={() => setThemePanelOpen(false)} />
      </div>
    </SnackbarProvider>
  )
}
